'use client';

// @boty/ui - Input component
// Wraps MUI TextField with Boty styling and RTL support

import type { ReactNode } from 'react';
import TextField, { type TextFieldProps } from '@mui/material/TextField';

export type InputProps = TextFieldProps & {
  // Add any custom props here
};

/**
 * Input - Styled MUI TextField with Boty theme and RTL support
 *
 * @param label - Input label text
 * @param error - Whether to show error state
 * @param helperText - Helper text below input (can be error message)
 * @param required - Whether field is required
 * @param disabled - Whether field is disabled
 * @param multiline - Enable multiline input (textarea)
 * @param rows - Number of rows when multiline
 * @param type - Input type ('text', 'email', 'password', 'number', etc.)
 * @param ...props - All standard MUI TextField props
 *
 * @example
 * ```tsx
 * // Basic input
 * <Input label="Email" type="email" />
 *
 * // With validation error
 * <Input
 *   label="Password"
 *   type="password"
 *   error={!!errors.password}
 *   helperText={errors.password?.message}
 * />
 *
 * // Multiline textarea
 * <Input label="Description" multiline rows={4} />
 * ```
 */
export function Input({
  variant = 'outlined',
  fullWidth = true,
  ...props
}: InputProps): ReactNode {
  return (
    <TextField
      variant={variant}
      fullWidth={fullWidth}
      {...props}
    />
  );
}

Input.displayName = 'Input';
